import {
  createContext,
  useContext,
  type PropsWithChildren,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  type RaThemeOptions,
  type ThemeType,
  useDataProvider,
  useNotify,
  useTheme,
} from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { useCosmos } from './CosmosContext';
import type { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { useLocalStorage } from '@mantine/hooks';
import type { TDashboardFilters } from 'screens/dashboard/Dashboard';
import { useTranslation } from 'react-i18next';
import BloxLoadingSplash from '../../components/generic/BloxLoadingSplash';
import { hasBetaProgram } from 'utility/experiments';
import {
  normalizeWorkflowId,
  useValidatedDefaultWorkflowId,
} from 'utility/workflowUtils';

export type ScopeType = 'site' | 'global';

// Site Settings.
export type DefaultPresentationsSetting = {
  article: string;
  image: string;
  collection: string;
};

export type EditorialSettings = {
  assistant_suggestions: boolean;
  content_extensions: boolean;
};

export type KPISettings = {
  editorial_bar: boolean;
  user_bar: boolean;
};

export type SiteUserSettings = {
  default_workflow: string;
  default_workflow_process?: string; // Optional, used for workflow process selection. Helps reduce the new asset missing this field when initialized.
  is_first_process_force_draft: boolean;
  default_presentations: DefaultPresentationsSetting;
  default_start_time?: string | null;
  default_author?: boolean;
};

export type SiteSettingsType = {
  editorial: EditorialSettings;
  kpi: KPISettings;
  user: SiteUserSettings;
  dashboards: {
    [dashboardId: string]: TDashboardFilters;
  };
};

export type SiteNamespaces = keyof SiteSettingsType;
export type SitePreferences = Partial<SiteSettingsType[SiteNamespaces]>;
type PartialSitePrefs = Partial<SiteSettingsType>;

// Global Settings.
export type GlobalUserSettings = {
  default_start_time: string | null;
  default_author: boolean;
  theme_mode?: 'light' | 'dark' | null;
  language?: string;
  session_tracking?: boolean;
};

export type GlobalSettingsType = {
  user: GlobalUserSettings;
};

export type GlobalNamespaces = keyof GlobalSettingsType;
export type GlobalPreferences = Partial<GlobalSettingsType[GlobalNamespaces]>;
type PartialGlobalPrefs = Partial<GlobalSettingsType>;

export type AppSettingsType = {
  site: {
    editorial: EditorialSettings;
    kpi: KPISettings;
    user: SiteUserSettings;
    dashboards?: {
      [dashboardId: string]: TDashboardFilters;
    };
  };
  global: {
    user: GlobalUserSettings;
  };
};

export type PartialAppSettingsType = {
  site: PartialSitePrefs;
  global: PartialGlobalPrefs;
};

export type SitePreferencesType = {
  [K in SiteNamespaces]: SitePreferences;
};

export type GlobalPreferencesType = {
  [K in GlobalNamespaces]: GlobalPreferences;
};

// Initial Settings for site and global scopes for all namespaces, keys are namespace type.
const initAppSettings: AppSettingsType = {
  site: {
    editorial: {
      assistant_suggestions: false,
      content_extensions: false,
    },
    kpi: {
      editorial_bar: false,
      user_bar: false, // AKA "community."
    },
    user: {
      default_workflow: '',
      default_workflow_process: '',
      is_first_process_force_draft: false,
      default_presentations: {
        article: '',
        image: '',
        collection: '',
      },
    },
    dashboards: {},
  },
  global: {
    user: {
      default_start_time: null,
      default_author: false,
      theme_mode: null,
      language: '',
      session_tracking: false,
    },
  },
};

export type AppSettingsCtxType = {
  isSettingsLoading: boolean;
  isSettingsFetching: boolean;
  isUpdating: boolean;
  appSettings: PartialAppSettingsType;
  refreshUserPreferences: (siteOrGlobal?: ScopeType) => Promise<unknown>;
  updateUserPreferences: (
    namespace: SiteNamespaces | GlobalNamespaces,
    newPreferences: SitePreferences | GlobalPreferences,
    siteOrGlobal?: ScopeType,
  ) => Promise<boolean>;
  theme: ThemeType | RaThemeOptions;
  updateTheme: (forcedTheme?: 'light' | 'dark') => void;
};

export const AppSettingsContext = createContext<AppSettingsCtxType | undefined>(
  undefined,
);

export const useAppSettings = (): AppSettingsCtxType => {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider!');
  }
  return context;
};

export const AppSettingsProvider = ({ children }: PropsWithChildren) => {
  const { t, i18n } = useTranslation();
  const { sessionConfig, activeSite, userInfo } = useCosmos();
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const notify = useNotify();
  const [isUpdating, setIsUpdating] = useState(false);
  const [raTheme, setRaTheme] = useTheme();
  const [, setUserPrefersCodeEditorThemeLS] = useLocalStorage({
    key: 'userPrefersCodeEditorTheme',
  });
  const [, setUserPrefersTrackingLS] = useLocalStorage({
    key: 'userPrefersTracking',
    defaultValue: 'false',
  });
  const [, setLanguageLS] = useLocalStorage({ key: 'language', defaultValue: 'en' });
  const strLoading = t('blox.general.loading');
  const hasBetaAccess = hasBetaProgram(sessionConfig, userInfo);
  const lastAppliedApiLanguageRef = useRef<string | undefined>(undefined);
  const hasInitializedLanguageFromApiRef = useRef(false);
  const hasInitializedThemeFromApiRef = useRef(false);
  const pendingThemePreferenceRef = useRef<'light' | 'dark' | null>(null);
  const lastAppliedApiThemeRef = useRef<'light' | 'dark' | null | undefined>(undefined);

  const updateTheme = useCallback(
    (forcedTheme?: 'light' | 'dark') => {
      const _theme = !!forcedTheme ? forcedTheme : raTheme === 'dark' ? 'light' : 'dark';
      pendingThemePreferenceRef.current = _theme;
      setRaTheme(_theme);
      // When changing global theme, also change
      // the code editor theme under key 'userPrefersCodeEditorTheme'.
      setUserPrefersCodeEditorThemeLS(_theme);
    },
    [setRaTheme, raTheme, setUserPrefersCodeEditorThemeLS],
  );

  // Track if we have attempted the initial prefs fetches
  // as we must block the first render until they complete.
  // This state should automatically reset when switching sites,
  // as this component would be fully unmounted/remounted...
  const [triedInitFetches, setTriedInitFetches] = useState({
    site: false,
    global: false,
  });

  // True when startup fetches have been attempted and resolved
  const renderReady = triedInitFetches.site && triedInitFetches.global;

  // Setup finalAppSettings state to provide via context
  const [finalAppSettings, setFinalAppSettings] =
    useState<PartialAppSettingsType>(initAppSettings);

  // Fetch the site/global settings/preferences per the active site.
  const siteSettingsHook = useQuery({
    queryKey: ['getUserPreferences', 'site', activeSite],
    queryFn: async () => {
      try {
        const response = await dataProvider.getUserPreferences('site');
        return response;
      } catch (error) {
        console.error('[siteSettingsResponse] Error:', error);
      } finally {
        // Attempted initial fetch, regardless of success or failure
        // we shouldnt block the entire app if the user prefs api fails...
        setTriedInitFetches((prev) => ({ ...prev, site: true }));
      }
    },
    enabled: Boolean(activeSite),
    refetchOnWindowFocus: false,
  });

  const globalSettingsHook = useQuery({
    queryKey: ['getUserPreferences', 'global', activeSite],
    queryFn: async () => {
      try {
        const response = await dataProvider.getUserPreferences('global');
        return response;
      } catch (error) {
        console.error('[globalSettingsResponse] Error:', error);
      } finally {
        setTriedInitFetches((prev) => ({ ...prev, global: true }));
      }
    },
    enabled: Boolean(activeSite),
    refetchOnWindowFocus: false,
  });

  const {
    data: siteSettings,
    refetch: refetchSiteSettings,
    isLoading: isSiteSettingsLoading,
    isFetching: isSiteSettingsFetching,
  } = siteSettingsHook;

  const {
    data: globalSettings,
    refetch: refetchGlobalSettings,
    isLoading: isGlobalSettingsLoading,
    isFetching: isGlobalSettingsFetching,
  } = globalSettingsHook;

  const isSettingsLoading = isSiteSettingsLoading || isGlobalSettingsLoading;
  const isSettingsFetching = isSiteSettingsFetching || isGlobalSettingsFetching;

  // Refresh the user preferences from the API, either site or global scope.
  const refreshUserPreferences = useCallback(
    async (siteOrGlobal: ScopeType = 'site') => {
      return siteOrGlobal === 'site' ? refetchSiteSettings() : refetchGlobalSettings();
    },
    [refetchSiteSettings, refetchGlobalSettings],
  );

  // PUT the updated preferences.
  const updateUserPreferences = useCallback(
    async (
      namespace: SiteNamespaces | GlobalNamespaces,
      newPreferences: SitePreferences | GlobalPreferences,
      siteOrGlobal: ScopeType = 'site',
    ): Promise<boolean> => {
      try {
        setIsUpdating(true);

        // Forming the main payload. Update the namespace with the new preferences.
        const updatedPreferences = {
          ...(siteOrGlobal === 'site'
            ? initAppSettings.site[namespace as SiteNamespaces]
            : initAppSettings.global[namespace as GlobalNamespaces]),
          // Merge site settings from api.
          ...(siteOrGlobal === 'site' &&
            siteSettings?.data?.[namespace as keyof typeof siteSettings.data]?.config),
          // Merge global settings from api.
          ...(siteOrGlobal === 'global' &&
            globalSettings?.data?.[namespace as keyof typeof globalSettings.data]
              ?.config),
          // Finally, apply the new preferences.
          ...newPreferences,
        };

        const response = await dataProvider.updateUserPreferences(
          namespace,
          updatedPreferences,
          siteOrGlobal,
        );

        // Notify the user about the successful update.
        if (response.status === 'success') {
          notify('User preferences updated successfully!', {
            type: 'success',
            autoHideDuration: 3000,
          });

          return true;
        }

        return false;
      } catch (error) {
        console.error('Failed to update user preferences:', error);
        // Notify the user about the error.
        notify('Failed to update user preferences.', {
          type: 'error',
          autoHideDuration: 3000,
        });

        return false;
      } finally {
        // Refetch the user preferences to get the latest data.
        refetchSiteSettings();
        refetchGlobalSettings();
        setIsUpdating(false);
      }
    },
    [
      siteSettings,
      globalSettings,
      dataProvider,
      notify,
      refetchSiteSettings,
      refetchGlobalSettings,
    ],
  );

  // Self-heal: if the site-scoped saved default workflow ID is present but invalid
  // (eg. deleted), clear the saved preference so consumers don't repeatedly try
  // to validate a stale ID and avoid console noise. This uses the shared
  // `useValidatedDefaultWorkflowId` hook to detect invalid candidates.
  const candidateDefaultWorkflowId = normalizeWorkflowId(
    siteSettings?.data?.user?.config?.default_workflow,
  );

  const {
    isPending: isDefaultWorkflowPending,
    isInvalidCandidate: isDefaultWorkflowCandidateInvalid,
  } = useValidatedDefaultWorkflowId(candidateDefaultWorkflowId);

  const clearedInvalidDefaultWorkflowRef = useRef<string | null>(null);

  useEffect(() => {
    if (!candidateDefaultWorkflowId) {
      return;
    }
    if (isDefaultWorkflowPending) {
      return;
    }
    if (!isDefaultWorkflowCandidateInvalid) {
      return;
    }

    // Avoid re-clearing for the same candidate during this session.
    if (clearedInvalidDefaultWorkflowRef.current === candidateDefaultWorkflowId) {
      return;
    }

    clearedInvalidDefaultWorkflowRef.current = candidateDefaultWorkflowId;

    // Clear the saved workflow prefs (site-scoped) — follow the same payload
    // shape as the user preferences UI.
    void (async () => {
      try {
        await updateUserPreferences(
          'user',
          {
            default_workflow: '',
            default_workflow_process: '',
            is_first_process_force_draft: false,
          },
          'site',
        );
      } catch (e) {
        // Do not escalate — this is a best-effort self-heal.
        // Keep console noise minimal per request.
      }
    })();
  }, [
    candidateDefaultWorkflowId,
    isDefaultWorkflowPending,
    isDefaultWorkflowCandidateInvalid,
    updateUserPreferences,
  ]);

  // Update finalAppSettings whenever
  // siteSettings and/or globalSettings change
  useEffect(() => {
    const newAppSettings: PartialAppSettingsType = {
      ...initAppSettings,
      site: {
        ...initAppSettings.site,
        ...siteSettings?.data?.config,
        editorial: {
          ...initAppSettings.site.editorial,
          ...siteSettings?.data?.editorial?.config,
        },
        kpi: {
          ...initAppSettings.site.kpi,
          ...siteSettings?.data?.kpi?.config,
        },
        user: {
          ...initAppSettings.site.user,
          ...siteSettings?.data?.user?.config,
        },
        dashboards: {
          ...initAppSettings.site.dashboards,
          ...siteSettings?.data?.dashboards?.config,
        },
      },
      global: {
        ...initAppSettings.global,
        ...globalSettings?.data?.config,
        user: {
          ...initAppSettings.global.user,
          ...globalSettings?.data?.user?.config,
        },
      },
    };

    // Update the finalAppSettings state
    setFinalAppSettings(newAppSettings);
  }, [siteSettings, globalSettings, hasBetaAccess]);

  useEffect(() => {
    const preferredTheme = globalSettings?.data?.user?.config?.theme_mode;

    // If API did not provide a value (still undefined), do nothing.
    if (typeof preferredTheme === 'undefined') {
      return;
    }

    // On initial app load, API theme is authoritative.
    if (!hasInitializedThemeFromApiRef.current) {
      hasInitializedThemeFromApiRef.current = true;

      lastAppliedApiThemeRef.current = preferredTheme;

      if (preferredTheme === 'light' || preferredTheme === 'dark') {
        if (raTheme !== preferredTheme) {
          setRaTheme(preferredTheme);
        }
        setUserPrefersCodeEditorThemeLS(preferredTheme);
      }

      if (preferredTheme === null) {
        try {
          localStorage.removeItem('RaStore.theme');
          setUserPrefersCodeEditorThemeLS(null as any);
        } catch (e) {
          // ignore
        }
      }

      pendingThemePreferenceRef.current = null;
      return;
    }

    if (preferredTheme === lastAppliedApiThemeRef.current) {
      if (pendingThemePreferenceRef.current === preferredTheme) {
        pendingThemePreferenceRef.current = null;
      }
      return;
    }

    lastAppliedApiThemeRef.current = preferredTheme;

    if (preferredTheme === 'light' || preferredTheme === 'dark') {
      // Only set if different to avoid unnecessary churn.
      if (raTheme !== preferredTheme) {
        setRaTheme(preferredTheme);
      }
      setUserPrefersCodeEditorThemeLS(preferredTheme);
      pendingThemePreferenceRef.current = null;
      return;
    }

    if (preferredTheme === null) {
      try {
        localStorage.removeItem('RaStore.theme');
        setUserPrefersCodeEditorThemeLS(null as any);
      } catch (e) {
        // ignore
      }
    }

    pendingThemePreferenceRef.current = null;
  }, [
    globalSettings?.data?.user?.config?.theme_mode,
    raTheme,
    setRaTheme,
    setUserPrefersCodeEditorThemeLS,
  ]);

  useEffect(() => {
    const preferredLanguage = finalAppSettings.global?.user?.language;
    const storedLanguage = localStorage.getItem('language');
    const languageSyncPending = localStorage.getItem('languageSyncPending') === 'true';

    // On initial app load, API language is authoritative.
    if (!!preferredLanguage && !hasInitializedLanguageFromApiRef.current) {
      if (i18n.language !== preferredLanguage) {
        i18n.changeLanguage(preferredLanguage);
        setLanguageLS(preferredLanguage);
      }
      hasInitializedLanguageFromApiRef.current = true;
      lastAppliedApiLanguageRef.current = preferredLanguage;
      localStorage.removeItem('languageSyncPending');
      return;
    }

    if (languageSyncPending) {
      if (
        !!preferredLanguage &&
        !!storedLanguage &&
        preferredLanguage === storedLanguage
      ) {
        localStorage.removeItem('languageSyncPending');
        lastAppliedApiLanguageRef.current = preferredLanguage;
      }

      return;
    }

    // After initialization, do not let stale API values overwrite newer local values.
    if (!!preferredLanguage && !!storedLanguage && preferredLanguage !== storedLanguage) {
      return;
    }

    if (
      !!preferredLanguage &&
      preferredLanguage !== lastAppliedApiLanguageRef.current &&
      i18n.language !== preferredLanguage
    ) {
      i18n.changeLanguage(preferredLanguage);
      setLanguageLS(preferredLanguage);
    }

    if (!!preferredLanguage) {
      lastAppliedApiLanguageRef.current = preferredLanguage;
    }
  }, [finalAppSettings.global?.user?.language, i18n, setLanguageLS]);

  // Sync API-backed session_tracking preference to local storage
  useEffect(() => {
    const sessionTracking = finalAppSettings.global?.user?.session_tracking;

    if (typeof sessionTracking === 'boolean') {
      try {
        setUserPrefersTrackingLS(sessionTracking ? 'true' : 'false');
      } catch (e) {
        // ignore
      }
    }
  }, [finalAppSettings.global?.user?.session_tracking, setUserPrefersTrackingLS]);

  // Show first loader until the initial fetches resolve
  // but dont show again after first attempts, eg if
  // updating prefs once rendered, should not show again.
  if (!renderReady) {
    return <BloxLoadingSplash message={strLoading} />;
  }

  return (
    <AppSettingsContext.Provider
      value={{
        appSettings: finalAppSettings,
        refreshUserPreferences,
        updateUserPreferences,
        isSettingsLoading: isSettingsLoading,
        isSettingsFetching: isSettingsFetching,
        isUpdating,
        theme: raTheme,
        updateTheme,
      }}
    >
      {children}
    </AppSettingsContext.Provider>
  );
};
